import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * The v2 chart kit — hand-rolled SVG, matching the design direction.
 *
 * Deliberately not chart.js: these are small, theme-native drawings (every colour is a CSS
 * variable so light/dark just work), and the console should not pull the dashboard's charting
 * stack into its bundle for four shapes. Interaction is one shared pattern: a pointer-following
 * tooltip fed by the nearest datum.
 */

export type ChartSeries = {
    key: string;
    label: string;
    /** CSS colour (var(--s1)…var(--s4) by convention). */
    color: string;
    values: number[];
    /** Draw the soft gradient fill under the line. */
    area?: boolean;
    /**
     * Put this series on its own right-hand scale.
     *
     * For pairs whose magnitudes have nothing to do with each other — a quarter-million vehicles
     * against six alerts — a shared axis is not a comparison, it is a flat line along the bottom.
     */
    axis?: "left" | "right";
};

type TipState = { x: number; y: number; html: React.ReactNode; align?: TipAlign; below?: boolean };
type TipAlign = "start" | "center" | "end";

function useTip() {
    const [tip, setTip] = useState<TipState>();
    const clear = useCallback(() => setTip(undefined), []);
    return { tip, setTip, clear };
}

/**
 * Which way the tooltip opens from its anchor.
 *
 * Cards clip their contents (`overflow-hidden`), so a box that always centred itself above the
 * point lost its left half on the first datum, its right half on the last, and its whole self on
 * a peak near the top. Rather than escaping the card, the box turns to face inward: it aligns to
 * whichever edge it is near, and flips below the point when there is no room above.
 */
const TIP_HALF_WIDTH = 96;
const TIP_HEIGHT = 92;

function placeTip(x: number, y: number, wrap: HTMLElement | null): { align: TipAlign; below: boolean } {
    const width = wrap?.clientWidth ?? 0;
    return {
        align: x < TIP_HALF_WIDTH ? "start" : width > 0 && x > width - TIP_HALF_WIDTH ? "end" : "center",
        below: y < TIP_HEIGHT,
    };
}

const TIP_SHIFT: Record<TipAlign, string> = { start: "0", center: "-50%", end: "-100%" };

const TipBox: React.FC<{ tip?: TipState }> = ({ tip }) =>
    tip ? (
        <div
            className="pointer-events-none absolute z-10 whitespace-nowrap rounded-[11px] border border-[var(--line-2)] bg-[var(--surface-3)] px-3.5 py-2.5 shadow-[0_16px_40px_rgba(0,0,0,.45)]"
            style={{
                left: tip.x,
                top: tip.below ? tip.y + 16 : tip.y - 10,
                transform: `translate(${TIP_SHIFT[tip.align ?? "center"]}, ${tip.below ? "0" : "-100%"})`,
            }}
        >
            {tip.html}
        </div>
    ) : null;

export const TipTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="mb-1.5 text-[13px] font-semibold">{children}</div>
);
export const TipRow: React.FC<{ color?: string; label: string; value: React.ReactNode }> = ({ color, label, value }) => (
    <div className="mt-1 flex items-center gap-2 text-[12.5px] text-[var(--muted)]">
        {color && <i className="h-2 w-2 flex-none rounded-full" style={{ background: color }} />}
        {label}
        <b className="ml-auto pl-5 font-semibold tabular-nums text-[var(--text)]">{value}</b>
    </div>
);

/** Series legend strip, shared by the cartesian charts. */
export const ChartLegend: React.FC<{ items: { label: string; color: string }[] }> = ({ items }) => (
    <div className="flex flex-wrap gap-5 px-5 pb-4">
        {items.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-2 text-[12.5px] text-[var(--muted)]">
                <i className="h-[11px] w-[11px] rounded-[3px]" style={{ background: s.color }} />
                {s.label}
            </span>
        ))}
    </div>
);

function smoothPath(pts: [number, number][]): string {
    if (pts.length === 0) return "";
    let d = `M${pts[0][0]},${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i];
        const b = pts[i + 1];
        const cx = (a[0] + b[0]) / 2;
        d += ` C${cx},${a[1]} ${cx},${b[1]} ${b[0]},${b[1]}`;
    }
    return d;
}

function niceMax(v: number): number {
    if (v <= 4) return 4;
    const mag = Math.pow(10, Math.floor(Math.log10(v)));
    const n = v / mag;
    const step = n <= 2 ? 2 : n <= 5 ? 5 : 10;
    return step * mag;
}

let gradientSeq = 0;

/**
 * The plot's width, in real pixels.
 *
 * The cartesian charts used to be drawn into a fixed 780-unit viewBox and stretched to fit, so
 * everything in them — axis numbers included — scaled with the card. Full-screen on a wide monitor
 * that meant 25px axis labels, and the outermost ones ran past the edge of the viewBox, where the
 * Card's own overflow clip cut them in half.
 *
 * Measuring instead makes one unit one pixel: type stays the size it was drawn at whatever the
 * card does, and the gutters can be sized to the labels that are actually going to be put in them.
 */
function usePlotWidth(): [React.RefObject<HTMLDivElement>, number] {
    const ref = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        // Only when it actually moved: setState with an equal value still costs a render pass, and
        // a resize drag delivers a lot of them.
        const measure = () => setWidth((w) => (w === el.clientWidth ? w : el.clientWidth));
        measure();
        // Two triggers, because they cover different things. The window event is the one this was
        // written for — going full screen on a wide monitor — and it fires even where the observer
        // does not (jsdom has no ResizeObserver at all). The observer catches the resizes no window
        // event describes: collapsing the navigation rail, or a card reflowing beside it.
        window.addEventListener("resize", measure);
        const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(measure);
        observer?.observe(el);
        return () => {
            window.removeEventListener("resize", measure);
            observer?.disconnect();
        };
    }, []);
    return [ref, width];
}

/** Narrower than this and there is no chart worth drawing; only reached before the first measure. */
const MIN_PLOT_W = 260;
const AXIS_FONT = 12;
/** Rough advance width of a character — close enough to size a gutter that must not clip. */
const CHAR_W = 0.62;
const widestLabel = (labels: string[]) => labels.reduce((w, l) => Math.max(w, l.length * AXIS_FONT * CHAR_W), 0);
/** The five gridline values an axis is labelled with. */
const AXIS_FRACTIONS = [0, 0.25, 0.5, 0.75, 1];
const axisTicks = (max: number) => AXIS_FRACTIONS.map((f) => Math.round(f * max).toLocaleString());

/**
 * Multi-series smoothed line chart with optional area fills and a crosshair tooltip.
 *
 * All series share the x labels. By default they also share one y scale; mark a series
 * `axis: "right"` to give it its own, which is what makes a small-count series readable beside a
 * large-count one. The right axis is drawn in that series' own colour, since two sets of numbers
 * down the sides of a chart are otherwise a puzzle.
 */
export function LineAreaChart({
    labels,
    series,
    height = 260,
    formatValue = (v: number) => v.toLocaleString(),
    tipTitle = (i: number) => labels[i],
    onHoverIndex,
    highlightIndex,
}: {
    labels: string[];
    series: ChartSeries[];
    height?: number;
    formatValue?: (v: number, key: string) => string;
    tipTitle?: (i: number) => React.ReactNode;
    /** Fires with the datum under the pointer, and undefined on leave. */
    onHoverIndex?: (index: number | undefined) => void;
    /** Draw the crosshair at this datum without the pointer being here — for linked charts. */
    highlightIndex?: number;
}): React.ReactElement {
    const { tip, setTip, clear } = useTip();
    const wrapRef = useRef<HTMLDivElement>(null);
    const [boxRef, measured] = usePlotWidth();
    const idsRef = useRef<string[]>();
    if (!idsRef.current) idsRef.current = series.map(() => `v2grad${gradientSeq++}`);

    const rightSeries = series.filter((s) => s.axis === "right");
    const leftSeries = series.filter((s) => s.axis !== "right");
    const hasRight = rightSeries.length > 0;

    // An empty side keeps a scale of its own so the axis labels stay sane; niceMax floors at 4.
    const maxLeft = niceMax(Math.max(1, ...leftSeries.flatMap((s) => s.values)));
    const maxRight = niceMax(Math.max(1, ...rightSeries.flatMap((s) => s.values)));

    const W = Math.max(measured, MIN_PLOT_W);
    const H = height;
    /*
     * Gutters sized to the numbers that are going in them, rather than to a fixed inset.
     *
     * Two things have to fit on each side: the axis figure, drawn 10px in from the plot, and half
     * of the outermost date label, which is centred on the axis itself. A quarter-million vehicles
     * is a wider label than any fixed inset was going to hold.
     */
    const xHalf = widestLabel(labels) / 2;
    const L = Math.ceil(Math.max(widestLabel(axisTicks(maxLeft)) + 12, xHalf, 24));
    const R = Math.ceil(Math.max(hasRight ? widestLabel(axisTicks(maxRight)) + 12 : 0, xHalf, 12));
    const T = 16;
    const B = 30;
    const iw = W - L - R;
    const ih = H - T - B;
    const n = Math.max(1, labels.length - 1);
    const x = (i: number) => L + (i / n) * iw;
    const yOn = (v: number, axis?: "left" | "right") => T + ih - (v / (axis === "right" ? maxRight : maxLeft)) * ih;
    const fractions = AXIS_FRACTIONS;
    const [hover, setHover] = useState<number>();

    const onMove = (e: React.MouseEvent<SVGRectElement>) => {
        const svg = (e.target as SVGRectElement).ownerSVGElement!;
        const r = svg.getBoundingClientRect();
        const wr = wrapRef.current!.getBoundingClientRect();
        const i = Math.max(0, Math.min(labels.length - 1, Math.round((((e.clientX - r.left) / r.width) * W - L) / (iw / n))));
        setHover(i);
        onHoverIndex?.(i);
        // Anchor to whichever series sits highest on screen at this x, each on its own scale.
        const topY = Math.min(...series.map((s) => yOn(s.values[i] ?? 0, s.axis)));
        const px = r.left - wr.left + x(i) * (r.width / W);
        const py = r.top - wr.top + topY * (r.height / H);
        setTip({
            x: px,
            y: py,
            ...placeTip(px, py, wrapRef.current),
            html: (
                <>
                    <TipTitle>{tipTitle(i)}</TipTitle>
                    {series.map((s) => (
                        <TipRow key={s.key} color={s.color} label={s.label} value={formatValue(s.values[i] ?? 0, s.key)} />
                    ))}
                </>
            ),
        });
    };

    return (
        <div ref={wrapRef} className="relative px-4 pb-4 pt-2">
            {/* The box is what gets measured; the drawing inside it is then one unit to one pixel.
                Out of flow, so a wide chart can never be what holds its own container open. */}
            <div ref={boxRef} className="relative" style={{ height: H }}>
                {measured > 0 && (
                    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="absolute left-0 top-0 block">
                        {fractions.map((f) => (
                            <g key={f}>
                                <line x1={L} y1={yOn(f * maxLeft)} x2={W - R} y2={yOn(f * maxLeft)} stroke="var(--grid)" strokeWidth={1} />
                                <text x={L - 10} y={yOn(f * maxLeft) + 4} textAnchor="end" fontSize={AXIS_FONT} fill="var(--faint)">
                                    {Math.round(f * maxLeft).toLocaleString()}
                                </text>
                                {hasRight && (
                                    <text
                                        x={W - R + 10}
                                        y={yOn(f * maxRight, "right") + 4}
                                        textAnchor="start"
                                        fontSize={AXIS_FONT}
                                        fill={rightSeries[0].color}
                                        opacity={0.85}
                                    >
                                        {Math.round(f * maxRight).toLocaleString()}
                                    </text>
                                )}
                            </g>
                        ))}
                        <defs>
                            {series.map((s, si) =>
                                s.area ? (
                                    <linearGradient key={s.key} id={idsRef.current![si]} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={s.color} stopOpacity=".32" />
                                        <stop offset="100%" stopColor={s.color} stopOpacity="0" />
                                    </linearGradient>
                                ) : null,
                            )}
                        </defs>
                        {series.map((s, si) => {
                            const pts = s.values.map((v, i) => [x(i), yOn(v, s.axis)] as [number, number]);
                            const line = smoothPath(pts);
                            return (
                                <g key={s.key}>
                                    {s.area && pts.length > 1 && (
                                        <path d={`${line} L${x(pts.length - 1)},${T + ih} L${L},${T + ih} Z`} fill={`url(#${idsRef.current![si]})`} />
                                    )}
                                    <path d={line} fill="none" stroke={s.color} strokeWidth={2.6} strokeLinejoin="round" />
                                </g>
                            );
                        })}
                        {labels.map((l, i) =>
                            labels.length > 16 && i % 2 ? null : (
                                <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize={AXIS_FONT} fill="var(--faint)">
                                    {l}
                                </text>
                            ),
                        )}
                        {/* The pointer wins; highlightIndex is the fallback so a sibling chart can drive
                            the crosshair without stealing it from someone actually hovering here. */}
                        {(hover ?? highlightIndex) !== undefined && (
                            <g>
                                {(() => {
                                    const at = (hover ?? highlightIndex)!;
                                    return (
                                        <>
                                            <line x1={x(at)} y1={T} x2={x(at)} y2={T + ih} stroke="var(--line-2)" strokeWidth={1} strokeDasharray="4 4" />
                                            {series.map((s) => (
                                                <circle
                                                    key={s.key}
                                                    cx={x(at)}
                                                    cy={yOn(s.values[at] ?? 0, s.axis)}
                                                    r={5}
                                                    fill={s.color}
                                                    stroke="var(--surface)"
                                                    strokeWidth={2}
                                                />
                                            ))}
                                        </>
                                    );
                                })()}
                            </g>
                        )}
                        <rect
                            x={L}
                            y={T}
                            width={iw}
                            height={ih}
                            fill="transparent"
                            className="cursor-crosshair"
                            onMouseMove={onMove}
                            onMouseLeave={() => {
                                clear();
                                setHover(undefined);
                                onHoverIndex?.(undefined);
                            }}
                        />
                    </svg>
                )}
            </div>
            <TipBox tip={tip} />
        </div>
    );
}

/** Stacked vertical bars — one bar per label, one segment per series. */
export function StackedBarChart({
    labels,
    series,
    height = 280,
    tipTitle = (i: number, total: number) => `${labels[i]} · ${total.toLocaleString()}`,
}: {
    labels: string[];
    series: ChartSeries[];
    height?: number;
    tipTitle?: (i: number, total: number) => React.ReactNode;
}): React.ReactElement {
    const { tip, setTip, clear } = useTip();
    const wrapRef = useRef<HTMLDivElement>(null);
    const [boxRef, measured] = usePlotWidth();

    const totals = labels.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
    const max = niceMax(Math.max(1, ...totals));

    const W = Math.max(measured, MIN_PLOT_W);
    const H = height;
    // As on the line chart: wide enough for the figures the axis is about to print. The date
    // labels sit on bar centres here, so only the left side has to be measured.
    const L = Math.ceil(Math.max(widestLabel(axisTicks(max)) + 12, 24));
    const R = 12;
    const T = 16;
    const B = 30;
    const iw = W - L - R;
    const ih = H - T - B;
    const bw = iw / Math.max(1, labels.length);
    const gap = Math.min(14, bw * 0.3);
    const gridLines = AXIS_FRACTIONS.map((f) => f * max);

    return (
        <div ref={wrapRef} className="relative px-4 pb-4 pt-2">
            <div ref={boxRef} className="relative" style={{ height: H }}>
                {measured > 0 && (
                    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="absolute left-0 top-0 block">
                        {gridLines.map((g) => (
                            <g key={g}>
                                <line x1={L} y1={T + ih - (g / max) * ih} x2={W - R} y2={T + ih - (g / max) * ih} stroke="var(--grid)" strokeWidth={1} />
                                <text x={L - 10} y={T + ih - (g / max) * ih + 4} textAnchor="end" fontSize={AXIS_FONT} fill="var(--faint)">
                                    {Math.round(g).toLocaleString()}
                                </text>
                            </g>
                        ))}
                        {labels.map((l, i) => {
                            const xx = L + i * bw + gap / 2;
                            const w = bw - gap;
                            let y0 = T + ih;
                            const segs = series.map((s) => {
                                const h = ((s.values[i] ?? 0) / max) * ih;
                                y0 -= h;
                                return { s, y: y0, h };
                            });
                            return (
                                <g key={i}>
                                    {segs.map(({ s, y, h }, si) =>
                                        h > 0 ? <rect key={s.key} x={xx} y={y} width={w} height={h} fill={s.color} rx={si === segs.length - 1 ? 4 : 0} /> : null,
                                    )}
                                    <text x={xx + w / 2} y={H - 8} textAnchor="middle" fontSize={AXIS_FONT} fill="var(--faint)">
                                        {l}
                                    </text>
                                    <rect
                                        x={L + i * bw}
                                        y={T}
                                        width={bw}
                                        height={ih}
                                        fill="transparent"
                                        className="cursor-crosshair"
                                        onMouseEnter={(e) => {
                                            const svg = (e.target as SVGRectElement).ownerSVGElement!;
                                            const r = svg.getBoundingClientRect();
                                            const wr = wrapRef.current!.getBoundingClientRect();
                                            const px = r.left - wr.left + (xx + w / 2) * (r.width / W);
                                            const py = r.top - wr.top + (T + ih - (totals[i] / max) * ih) * (r.height / H);
                                            setTip({
                                                x: px,
                                                y: py,
                                                ...placeTip(px, py, wrapRef.current),
                                                html: (
                                                    <>
                                                        <TipTitle>{tipTitle(i, totals[i])}</TipTitle>
                                                        {series.map((s) => (
                                                            <TipRow key={s.key} color={s.color} label={s.label} value={s.values[i] ?? 0} />
                                                        ))}
                                                    </>
                                                ),
                                            });
                                        }}
                                        onMouseLeave={clear}
                                    />
                                </g>
                            );
                        })}
                    </svg>
                )}
            </div>
            <TipBox tip={tip} />
        </div>
    );
}

/** Horizontal labelled bars against a quiet track — distributions and type breakdowns. */
export function HBarList({
    rows,
    formatValue = (v: number) => String(v),
    maxValue,
}: {
    rows: { label: string; value: number; color: string }[];
    formatValue?: (v: number) => string;
    maxValue?: number;
}): React.ReactElement {
    const max = Math.max(1, maxValue ?? Math.max(...rows.map((r) => r.value)));
    return (
        <div className="flex flex-col gap-3 px-5 pb-5 pt-3">
            {rows.map((r) => (
                <div key={r.label} className="flex items-center gap-3">
                    <span className="w-[124px] flex-none truncate text-right text-[12.5px] text-[var(--muted)]">{r.label}</span>
                    <span className="relative h-[22px] min-w-0 flex-1 overflow-hidden rounded-[7px] bg-[var(--track)]">
                        <span
                            className="absolute inset-y-0 left-0 rounded-[7px]"
                            style={{ width: `${Math.max((r.value / max) * 100, r.value > 0 ? 2 : 0)}%`, background: r.color }}
                        />
                    </span>
                    <span className="w-[52px] flex-none text-[12.5px] font-semibold tabular-nums">{formatValue(r.value)}</span>
                </div>
            ))}
        </div>
    );
}

/** Semicircular gauge: big value, caption, and a colour that carries the verdict. */
export function GaugeArc({
    fraction,
    big,
    caption,
    color = "var(--safe)",
}: {
    fraction: number;
    big: string;
    caption: string;
    color?: string;
}): React.ReactElement {
    const cx = 150;
    const cy = 150;
    const r = 108;
    const arc = (a0: number, a1: number) => {
        const p = (a: number) => {
            const rad = ((a - 180) * Math.PI) / 180;
            return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
        };
        const s = p(a0);
        const e = p(a1);
        return `M${s[0]},${s[1]} A${r},${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${e[0]},${e[1]}`;
    };
    const clamped = Math.max(0, Math.min(1, fraction));
    return (
        <div className="px-5 pb-5 pt-2 text-center">
            <svg viewBox="0 0 300 172" className="mx-auto block h-auto w-full max-w-[320px]">
                <path d={arc(0, 180)} fill="none" stroke="var(--track)" strokeWidth={30} strokeLinecap="round" />
                {/* Value expressed by arc sweep, not dash animation — a wrong gauge beats no flourish. */}
                {clamped > 0.005 && (
                    <path d={arc(0, 180 * clamped)} fill="none" stroke={color} strokeWidth={30} strokeLinecap="round" />
                )}
                <text x={cx} y={cy - 24} textAnchor="middle" fill="var(--text)" style={{ font: "700 38px inherit", letterSpacing: "-.03em" }}>
                    {big}
                </text>
                <text x={cx} y={cy + 2} textAnchor="middle" fill="var(--muted)" style={{ font: "500 14px inherit" }}>
                    {caption}
                </text>
            </svg>
        </div>
    );
}
